<template>
  <wiskInputGroup @errorCountChanged="setValidState" :disabled="disabled" style="min-height: 580px;" @operationsChange="onChange" v-if="localVenue" ref="inputGroup">

    <div v-if="!localVenue.id" style="min-height: 600px;">
      <div class="mt-3 mb-4 d-block" style="height: 40px;" v-if="hasOtherVenues">
        <infoTooltip inline :scale="1.2" helpKey="newVenueCreationInfoForExistingUser" />
      </div>

      <venueFinder :venue="localVenue" @change="venueFinderChanged" autofocus :preloadTitle="preloadTitle" />

      <wiskSelect infoTooltipKey="4e85f2b4-586a-4782-a98c-b663d9a930bd" :label="translations.txtVenueOperationsVenueType" :modelValue="localVenue.venue_type" required operation="venue_type"
        :items="venueTypes" :multiselectOptions="{}" @operation="onSingleChange" displayAsButtonGroup />

      <wiskSelect infoTooltipKey="c57f2fd2-4fa7-46ff-8264-f24e705a1989" :label="translations.txtVenueOperationsPopulateData" v-model="populateMethod"
        :items="newVenueDataPopulateMethods" displayAsButtonGroup />

      <template v-if="populateMethod === 2">
        <wiskInput v-if="godMode && currentVenue?.id" infoTooltipKey="45689096-e3b5-4668-8f6a-64e8acdb8ea9" :label="translations.translate('tplVenueOperationsCentralItemsList', { '{a}': currentVenue.title })"
          operation="use_parent_items" class="" @operation="onSingleChange" v-model="localVenue.use_parent_items" @update:modelValue="onUseParentItemsChange" inputType="checkbox" disabled triggerInputOnLoad>

          <wiskInput infoTooltipKey="29eda0ea-8c61-4cb5-94e6-afb85f2fedeb" v-if="godMode && localVenue.use_parent_items" :label="translations.translate('tplVenueOperationsCentralPOSItemsList', { '{a}': currentVenue.title })"
            operation="use_parent_pos_items" class="" @operation="onSingleChange" v-model="localVenue.use_parent_pos_items" inputType="checkbox" />
        </wiskInput>

      </template>

      <template v-if="populateMethod === 1">
        <wiskSelect infoTooltipKey="89f8dc06-1b76-416b-8aa7-de1c80e07a9e" operation="copy_data_from_venue" name="copyData"
          :items="copyDataOptions" displayAsButtonGroup :buttons="false" v-model="localVenue.copy_data_from_venue" />
      </template>

      <fieldset v-if="godMode" class="mt-5" style="position: relative; padding-left: 31px; padding-right: 31px; width: 100%;">
        <legend class="mb-3" style="">
          {{ translations.txtVenueOperationsAdminFields }}
        </legend>

        <b-row>
          <b-col class="venue-settings-col" cols="12" md="8">
            <paymentInfo :venue="localVenue" @change="paymentInfoChanged" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="a615780b-7c93-4e6e-aa2c-ef4f4ca51e66" :label="translations.txtVenuesStripeId" operation="stripe_id" operationEmpty="stripe_id_clear"
              :modelValue="localVenue.stripe_id" :fitChildren="!!localVenue.stripe_id">
              <a v-if="localVenue.stripe_id" ref="noopener" :href="`https://dashboard.stripe.com/customers/${localVenue.stripe_id}`" target="_blank"> {{ translations.txtVenuesGoToStripe }} </a>
            </wiskInput>
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="82299dbd-af65-40b3-acc5-55581616ed81" :label="translations.txtVenueOperationsTesting" :modelValue="localVenue.testing" inputType="checkbox"
              operation="testing" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskSelect infoTooltipKey="e6545aa2-2103-4ebd-b84b-d43fb63bdc41" label="Invoice scanning type" :modelValue="localVenue.draft_invoices_settings.type"
              :items="draftInvoiceSettingTypes" :multiselectOptions="{}" @update:modelValue="onDraftInvoiceSettingChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskSelect infoTooltipKey="53ab48b8-9970-4976-ad14-c6473a73311f" label="Organization" :modelValue="localVenue.organization_id"
              :items="organizations" operation="organization_id" operationEmpty="organization_id_clear" :addNewItem="addNewOrganizationMultiselectAction" :loading="loadingOrganizations" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="5c526c35-c30f-4731-ae41-45a8cc04f109" label="Is part of a hotel" v-model="localVenue.is_part_of_hotel" inputType="checkbox"
              operation="is_part_of_hotel" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3" v-if="localVenue.is_part_of_hotel">
            <wiskInput :label="translations.txtVenueOperationsLocationCode" inputType="text" operation="location_code" v-model="localVenue.location_code"
              :validations="[locationCodeValidation]" :helperText="translations.txtVenueOperationsLocationCodeHelper" required />
          </b-col>
        </b-row>
      </fieldset>

    </div>
    <div v-else class="pb-2">
      <b-row class="mb-2">
        <b-col class="venue-settings-col" cols="12" md="3">
          <div class="position-relative images-controls-wrapper">
            <imageView :src="localVenue.white_label" filterDisplay="smallLandscape" @click="editVenueImage" class="mb-3" style="max-width: 200px;" />
            <div class="position-absolute" v-if="localVenue.white_label" style="top: 0; right: 0;">
              <b-button variant="link" @click="removeVenueImage" class="bt-delete-image p-1" v-tooltip="translations.txtGenericRemove">
                <icon name="wisk-trash" class="image-view-overlay-icon text-danger" :scale="1" />
              </b-button>
            </div>
            <div class="position-absolute" style="bottom: 0; right: 0;">
              <b-button variant="link" @click="editVenueImage" class="bt-delete-image p-1" v-tooltip="translations.txtGenericEdit">
                <icon name="wisk-edit" class="image-view-overlay-icon text-primary" :scale="1" />
              </b-button>
            </div>
          </div>
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="8">
          <venueFinder :venue="localVenue" @change="venueFinderChanged" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput label="Short name" inputType="text" operation="short_title" :modelValue="localVenue.short_title" :validations="[shortTitleValidation]"
            helperText="A shorter venue title for display." @operation="onSingleChange" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput :label="translations.txtVenueOperationsLocationCode" inputType="text" operation="location_code" :modelValue="localVenue.location_code"
            :validations="[locationCodeValidation]" :helperText="translations.txtVenueOperationsLocationCodeHelper" :required="!!localVenue.is_part_of_hotel"
            :operationConfirm="localVenue.location_code ? { message: translations.txtVenueOperationsLocationCodeChangeConfirm } : null"
            @operation="onSingleChange({ ...$event, value: ($event.value || '').trim().toUpperCase() })" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskSelect infoTooltipKey="eecab665-2d13-4984-9a75-dce6c791b876" :label="(translations.txtGenericLanguage === 'Language' && translations.txtGenericLanguage) || translations.txtGenericLanguage + ' / Language'"
            :modelValue="localVenue.language" operation="language" :items="languages" :multiselectOptions="{}" @operation="onSingleChange" required />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskSelect infoTooltipKey="5639bca2-6b5d-455e-95ba-435203086ae3" :label="translations.txtGenericDisplayCurrency" :modelValue="selectedCurrency" required operation="display_currency"
            :items="currencies" @operation="onSingleChange" trackBy="code" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput infoTooltipKey="39d5ac77-82c2-421d-a4f5-443c7bac582c" :label="translations.txtVenueOperationsBusinessDayStartHour" :modelValue="`${venue.business_day_starting_hour}:00`" inputType="timePicker"
            operation="business_day_starting_hour" class="" @operation="onSingleChange({ ...$event, value: parseInt($event.value, 10), from: venue.business_day_starting_hour })" :timePickerOptions="{ minutesDisabled: true }" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskSelect infoTooltipKey="40b3a172-1fba-4b75-92c9-fe7983b08fea" :label="translations.txtVenueOperationsPartialMeasuringType" :modelValue="localVenue.partial_measuring_type" operation="partial_measuring_type"
            :items="partialMeasuringTypes" :multiselectOptions="{}" @operation="onSingleChange" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput infoTooltipKey="5a0305ac-27ea-49bf-97c0-9d3b956b3f92" :label="translations.txtVenueOperationsIdealStockDays" :modelValue="localVenue.ideal_stock_days" inputType="number"
            operation="ideal_stock_days" :decimals="1" decimalsAsNeeded @operation="onSingleChange" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput infoTooltipKey="08e40eeb-ca65-4ed5-830f-157b40013b40" :label="translations.txtVenueOperationsInventoryHours" :modelValue="localVenue.inventory_hours_before_wisk" inputType="number"
            operation="inventory_hours_before_wisk" :decimals="1" decimalsAsNeeded @operation="onSingleChange" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput infoTooltipKey="9c3bc5fe-47ce-4721-8bb8-2f81d077a84e" :label="translations.txtVenueOperationsInventoryLockDaysAfterApproving" :modelValue="localVenue.days_to_lock_inventory_after_approving" inputType="number"
            operation="days_to_lock_inventory_after_approving" @operation="onSingleChange" :min="1" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput infoTooltipKey="c1ae4f2e-fc15-44f1-a11c-81fb628484e7" :label="translations.txtVenueOperationsInventoryLockDaysAfterUnlocking" :modelValue="localVenue.days_to_lock_inventory_after_unlocking" inputType="number"
            operation="days_to_lock_inventory_after_unlocking" @operation="onSingleChange" :min="1" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput infoTooltipKey="d5397e14-c28f-45e5-9289-8e8175b0f75b" :label="translations.txtVenueOperationsRealtimeStockRecalculationDays" :modelValue="localVenue.real_time_stock_threshold_days" inputType="number"
            operation="real_time_stock_threshold_days" @operation="onSingleChange" :min="1" :max="365" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput infoTooltipKey="eb225c3f-7489-46cd-a46b-3048fe3bb1c0" :label="translations.txtVenueOperationsTransfers" :modelValue="localVenue.transfers" inputType="checkbox"
            operation="transfers" class="float-start me-3 mb-2" @operation="onSingleChange" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput infoTooltipKey="33ed65ed-77d5-4c61-be19-b3ac79276d20" :label="translations.txtVenueOperationsPriceRetail" :modelValue="localVenue.price_retail" inputType="checkbox"
            operation="price_retail" class="float-start me-3 mb-2" @operation="onSingleChange" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3" v-if="priceFeeAvailable">
          <wiskInput infoTooltipKey="2c7ab526-3d34-442c-943c-5361a2598e4b" v-if="priceFeeAvailable" :label="translations.txtVenueOperationsPriceFee" :modelValue="localVenue.price_fee" inputType="checkbox"
            operation="price_fee" class="float-start me-3 mb-2" @operation="onSingleChange" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput infoTooltipKey="6c9c8794-7893-4abf-9922-0c3ad908a327" :label="translations.txtGenericScale" :modelValue="localVenue.scale" inputType="checkbox"
            operation="scale" class="float-start me-3 mb-2" @operation="onSingleChange" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput infoTooltipKey="4f378036-f389-489c-9210-55d5f816906d" :label="translations.txtVenueOperationsWeightedAveragePricing" :modelValue="localVenue.weighted_average_pricing" inputType="checkbox"
            operation="weighted_average_pricing" class="float-start me-3 mb-2" @operation="onSingleChange" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput infoTooltipKey="e9f1fca5-bea1-4a6d-8579-08b0efa02f90" :label="translations.txtVenueOperationsRequireIntakePhoto" :modelValue="localVenue.require_intake_photos" inputType="checkbox"
            operation="require_intake_photos" class="float-start me-3 mb-2" @operation="onSingleChange" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3">
          <wiskInput infoTooltipKey="e482eea1-63b1-492e-8edb-2c1ec3005512" :label="translations.txtVenueOperationsLiveSales" :modelValue="localVenue.live_sales" inputType="checkbox"
            operation="live_sales" class="float-start me-3 mb-2" @operation="onSingleChange" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="6">
          <barcodePrinterSetup inline />
        </b-col>
        <b-col v-if="measurementsByType" class="venue-settings-col" cols="12" md="3">
          <wiskSelect infoTooltipKey="cbc8f0ad-1d13-43e7-8667-029d06fe9503" :label="translations.txtVenueOperationsDefaultUMVolume" :modelValue="localVenue.default_um_volume"
            :items="measurementsVolume" operation="default_um_volume" @operation="onSingleChange" />
        </b-col>
        <b-col v-if="measurementsByType" class="venue-settings-col" cols="12" md="3">
          <wiskSelect infoTooltipKey="3d4cc885-15a5-4aa6-a527-2ca10f2e8262" :label="translations.txtVenueOperationsDefaultUMWeight" :modelValue="localVenue.default_um_weight"
            :items="measurementsWeight" operation="default_um_weight" @operation="onSingleChange" />
        </b-col>
        <b-col class="venue-settings-col" cols="12" md="3" v-if="localVenue.custom_attributes && localVenue.custom_attributes.api_key">
          <wiskInput infoTooltipKey="23b82ec4-0d5e-452b-a780-99e0ec966298" :label="translations.txtGenericApiKey" :modelValue="localVenue.custom_attributes.api_key" readonly inputType="text"
            v-if="localVenue.custom_attributes && localVenue.custom_attributes.api_key" />
        </b-col>
        <b-col v-if="showChangeSubscriptionPlatform" class="venue-settings-col" cols="12" md="3">
          <b-button @click="onSingleChange({ type: 'migrate_to_stripe_subscription' })" variant="primary" size="sm" class="mb-4">
            {{ translations.txtVenueOperationsChangeSubscriptionPlatform }}
          </b-button>
        </b-col>
      </b-row>

      <b-row>
        <b-col class="venue-settings-col" cols="12" md="6" v-if="godMode">
          <wiskInput inputType="text" :passwordField="!showApiKey" readonly :label="translations.txtGenericVenueApiKey" :modelValue="venue.api_key" fitChildren :fitChildrenCols="1">
            <b-copy v-if="showApiKey" variant="light" class="ms-1" :copyText="venue.api_key" />
            <b-button v-else size="sm" variant="light" class="ms-1" @click="showApiKey = true">
              <icon style="margin-top: 2px;" class="text-primary" name="wisk-show" :scale="1" />
            </b-button>
          </wiskInput>
        </b-col>

        <b-col class="venue-settings-col" cols="12" md="6">
          <b-button variant="primary" size="sm" @click="resetLocalData">
            {{ translations.txtVenueOperationsResetLocalVenueData }}
          </b-button>
        </b-col>
      </b-row>

      <venuePOSIntegrations @operation="onSingleChange" :venueId="venue.id" />

      <fieldset v-if="localVenue.accounting_enabled && localVenue.accounting_integration" class="" style="position: relative; padding-left: 31px; padding-right: 31px;">
        <legend class="mb-3" style="">
          {{ translations.txtVenueOperationsAccountingIntegration }}
        </legend>

        <b-row>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskSelect infoTooltipKey="503b90c1-4bed-4b47-8399-ea5757998f4b" :label="translations.txtGenericType" :modelValue="localVenue.accounting_integration.type" operation="accounting_integration"
              :items="accountingIntegrationTypes" :multiselectOptions="{}" @operation="onSingleChange({ ...$event, value: { type: $event.value, status: $event.value === 'quickbooks' ? 'not_set_up' : 'active' } })" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3" v-if="venue?.accounting_integration?.is_using_classes && localVenue.accounting_integration?.type === 'quickbooks'">
            <wiskSelect infoTooltipKey="8e35e063-0edc-4b77-b40c-60f1265e69e3" label="Default Quickbooks class" :modelValue="localVenue?.accounting_integration?.default_venue_class_id"
              :items="accountingVenueClasses" :multiselectOptions="{}" operation="default_accounting_venue_class_id" operationEmpty="default_accounting_venue_class_id_clear" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="6" v-if="localVenue.accounting_enabled && localVenue.accounting_integration?.type === 'quickbooks'">
            <b-button variant="primary" size="sm" @click="goToQuickbooks" preventDoubleClick>
              {{ translations.txtVenueOperationsAccountingSetup }}
            </b-button>
            <span class="ms-3" :class="{ 'text-danger': !accountingIntegrated, 'text-success': accountingIntegrated }">
              {{ accountingIntegrated ? translations.txtVenueOperationsAccountingSetupDone : translations.txtVenueOperationsAccountingSetupNone }},
            </span>
            <span class="ms-1 text-bold" :class="{ 'text-danger': localVenue.accounting_integration.status === 'expired', 'text-success': localVenue.accounting_integration.status === 'active' }">
              {{ localVenue.accounting_integration.status }}
            </span>
          </b-col>
        </b-row>
      </fieldset>

      <fieldset v-if="godMode" class="" style="position: relative; padding-left: 31px; padding-right: 31px;">

        <legend class="mb-3" style="">
          {{ translations.txtVenueOperationsAdminFields }}
        </legend>

        <b-row>
          <b-col class="venue-settings-col" cols="12" md="6">
            <paymentInfo :venue="localVenue" @change="paymentInfoChanged" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="a615780b-7c93-4e6e-aa2c-ef4f4ca51e66" :label="translations.txtVenuesStripeId" operation="stripe_id" operationEmpty="stripe_id_clear"
              :modelValue="localVenue.stripe_id" @operation="onSingleChange" :fitChildren="!!localVenue.stripe_id">
              <a v-if="localVenue.stripe_id" ref="noopener" :href="`https://dashboard.stripe.com/customers/${localVenue.stripe_id}`" target="_blank"> {{ translations.txtVenuesGoToStripe }} </a>
            </wiskInput>
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskSelect infoTooltipKey="53ab48b8-9970-4976-ad14-c6473a73311f" label="Organization" :modelValue="localVenue.organization_id" :loading="loadingOrganizations"
              :items="organizations" operation="organization_id" operationEmpty="organization_id_clear" @operation="onSingleChange" :addNewItem="addNewOrganizationMultiselectAction" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskSelect infoTooltipKey="b18cc2d4-275c-4c7d-a642-ec8098eb696d" :label="translations.txtVenueOperationsVenueType" :modelValue="localVenue.venue_type" required operation="venue_type"
              :items="venueTypes" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskSelect infoTooltipKey="37de3a0b-49f4-4505-87b5-2c92740f165b" :label="translations.txtVenueOperationsSalesRep" :modelValue="localVenue.sales_rep" operation="sales_rep"
              :items="salesReps" :multiselectOptions="{}" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="82299dbd-af65-40b3-acc5-55581616ed81" :label="translations.txtVenueOperationsTesting" :modelValue="localVenue.testing" inputType="checkbox"
              operation="testing" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="19b4b93d-a5d4-4a04-bdc6-4f3ad260f7dc" :label="translations.txtVenueTransferRequestEnabled" :modelValue="localVenue.multivenue_enabled" inputType="checkbox"
              operation="multivenue_enabled" class="float-start me-3 mb-2" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskSelect infoTooltipKey="e6545aa2-2103-4ebd-b84b-d43fb63bdc41" label="Invoice scanning type" :modelValue="localVenue.draft_invoices_settings.type"
              :items="draftInvoiceSettingTypes" :multiselectOptions="{}" @update:modelValue="onDraftInvoiceSettingChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="895a1db9-0860-434d-b66f-63fdd52550da" label="Draft Invoices - Final Approval Needed" :modelValue="localVenue.draft_invoice_final_approval_needed"
              inputType="checkbox" operation="draft_invoice_final_approval_needed" class="me-3 mb-2" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="668e5f65-462e-4151-9de8-d53b21b659e9" label="Accounting Integration" :modelValue="localVenue.accounting_enabled" inputType="checkbox"
              operation="accounting_enabled" @operation="onSingleChange" class="mb-0" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="8d0ca9f6-703d-4df0-8a24-1d32fccd50d0" label="Taxes" :modelValue="localVenue.taxes_enabled" inputType="checkbox"
              operation="taxes_enabled" @operation="onSingleChange" class="mb-0" />
          </b-col>
          <!-- <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="94bf03f7-b2b2-41df-8610-09bdce87125b" label="Distributor Venue" :modelValue="localVenue.venue_as_distributor" inputType="checkbox"
              operation="venue_as_distributor" class="float-start me-3 mb-2" @operation="onSingleChange" />
          </b-col> -->
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="b0b6595f-e958-4d3f-9156-dcf79b2eac81" :label="translations.txtVenueOperationsFintechLabel" operation="fintech_label"
              :modelValue="localVenue.fintech_label" @operation="onSingleChange" />
          </b-col>
          <!-- Super users only: the API refuses this operation from anyone else. -->
          <b-col v-if="venue.id && user?.is_super_user" class="venue-settings-col" cols="12" md="3">
            <wiskInput label="AI chat monthly limit" inputType="number" operation="ai_chat_monthly_limit" :modelValue="aiChatEffectiveMonthlyLimitCents / 100"
              prefix="$" :decimals="2" decimalsAsNeeded :min="0" :max="aiChatMaxMonthlyLimitCents / 100" :helperText="aiChatMonthlyLimitHelperText"
              @operation="onAiChatMonthlyLimitChange">
              <template v-slot:append>
                <b-button v-if="aiChatMonthlyLimitCents !== null" variant="link" size="sm" class="p-0 ms-2" @click="onSingleChange({ type: 'ai_chat_monthly_limit_clear' })">
                  Reset to the ${{ aiChatDefaultMonthlyLimitCents / 100 }} default
                </b-button>
              </template>
            </wiskInput>
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="12b05e38-d600-4efc-8e64-19b17766d488" :label="translations.txtVenueOperationsSalesConversation" :modelValue="localVenue.support_sales_conversion"
              operation="support_sales_conversion" @operation="onSingleChange" inputType="checkbox" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="4eb9f1b6-b0e7-4a79-b19d-2ce9b21b96ee" :label="translations.txtVenueOperationsShowCurrentDistributorPrices" :modelValue="localVenue.show_current_distributor_prices"
              operation="show_current_distributor_prices" @operation="onSingleChange" inputType="checkbox" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="" :label="translations.txtVenueOperationsInventoryAudio" :modelValue="localVenue.audio_inventory_enabled"
              operation="audio_inventory_enabled" @operation="onSingleChange" inputType="checkbox" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="da6d1e2a-10c7-4dac-a59d-e57956b9d9d7" label="BeerSaver integration" :modelValue="localVenue.beer_saver_integration?.type === 'logo'"
              inputType="checkbox" @input="beerSaverIntegrationChanged" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="a74a8e8c-6b7b-467e-8c8b-2dbdf8255fdc" label="Hide venue progress" :modelValue="localVenue.hide_progress_bar"
              inputType="checkbox" operation="hide_progress_bar" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="f34c6cf0-2d3d-4a12-89eb-1d196d7ebd9c" label="Allow inventory import" :modelValue="localVenue.allow_inventory_import"
              inputType="checkbox" operation="allow_inventory_import" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="5c526c35-c30f-4731-ae41-45a8cc04f109" label="Is a part of hotel" :modelValue="localVenue.is_part_of_hotel" inputType="checkbox"
              operation="is_part_of_hotel" :validations="[partOfHotelValidation]" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput label="Transfers generate an invoice by default in the receiving venue" infoTooltipKey="venueDefaultShouldGenerateInvoiceInfoTooltip"
              :modelValue="localVenue.default_should_generate_invoice" inputType="checkbox"
              operation="default_should_generate_invoice" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="0caa5b90-4ad0-4977-9a21-5a6fa9c09647" label="Use master beverage database" :modelValue="localVenue.use_master_beverage_database" inputType="checkbox"
              operation="use_master_beverage_database" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="e32dc8ea-d24c-4845-a701-602cfa401959" label="Enable historical real time stock" :modelValue="localVenue.show_historical_real_time_stock" inputType="checkbox"
              operation="show_historical_real_time_stock" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput infoTooltipKey="44eafaf4-0b0a-4019-8826-c574f89ba949" label="Enable Variance per Month" :modelValue="localVenue.enable_variance_for_month" inputType="checkbox"
              operation="enable_variance_for_month" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput :label="translations.txtVenueOperationsDisableAutoAddDuringFirstInventory" :modelValue="localVenue.disable_auto_add_during_first_inventory" inputType="checkbox"
              operation="disable_auto_add_during_first_inventory" @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" cols="12" md="3">
            <wiskInput :label="translations.txtVenueOperationsUseBatchFirstStock" :modelValue="localVenue.use_batch_first_stock" inputType="checkbox"
              operation="use_batch_first_stock" @operation="onSingleChange" />
          </b-col>
          <b-col cols="12"></b-col>
          <b-col v-if="localVenue?.draft_invoices_settings?.type === 'wisk'" cols="12" md="6">
            <wiskInput inputClass="mt-2" infoTooltipKey="06c5720f-fa52-45dd-a1c4-62b2701b3c72" :label="translations.txtVenueOperationsInvoiceProcessingNotes" operation="internal_notes"
            :modelValue="localVenue.internal_notes" multiline autoHeightTextArea @operation="onSingleChange" />
          </b-col>
          <b-col class="venue-settings-col" style="text-align: right;" cols="12">
            <b-button variant="outline-primary" size="sm" @click="sendVenueReport('weekly')" class="ms-2">
              Send weekly report
            </b-button>

            <b-button variant="outline-primary" size="sm" @click="sendVenueReport('daily')" class="ms-2">
              Send daily report
            </b-button>

            <b-button variant="outline-primary" size="sm" @click="loadCleanupPOSItemsPreview" class="ms-2">
              Remove unused POS items
            </b-button>

            <b-button variant="outline-primary" size="sm" @click="previewConvertToHotelSharedItems" :disabled="convertLoading" class="ms-2">
              Convert to Hotel Shared Items
              <span v-if="convertLoading && convertPreviewProgress">({{ convertPreviewProgress }}%)</span>
              <wiskLoading v-if="convertLoading" loading class="position-relative float-end ms-2" />
            </b-button>

            <b-button variant="outline-primary" size="sm" @click="resyncVenueData" class="ms-2">
              {{ translations.txtVenueOperationsResyncVenueData }}
            </b-button>

            <b-button variant="outline-primary" size="sm" @click="resyncAreasData" class="ms-2">
              {{ translations.txtVenueOperationsResyncAreasData }}
            </b-button>
          </b-col>
        </b-row>

        <b-row>
          <venuesSharedCustomFields />
        </b-row>
      </fieldset>

      <customFields target="venue" :item="localVenue" @operation="onSingleChange" />

    </div>
    <confirm ref="confirmDialog" />
    <confirm ref="confirmDialogResyncVenueData">
      <wiskInput inputType="checkbox" v-model="resyncVenueDataIncreaseVersion" label="Force full data download for all users of the venue" infoTooltipKey="resyncVenueDataIncreaseVersionTooltip" />
    </confirm>
    <confirm ref="confirmDialogChildVenueSelector" :okDisabledManual="!selectedChildVenues.length">
      <wiskSelect :items="venuesHighLevel" trackBy="id" label="Child venues" infoTooltipKey="630346f8-9d9c-4119-9214-00adf460b6aa"
          v-model="selectedChildVenues" fixedWrapper multiple />
    </confirm>
  </wiskInputGroup>

  <wiskModal title="Remove unused POS items" :visible="posItemsCleanupDialog" @hide="posItemsCleanupDialog = false" @ok="confirmCleanupPOSItems" size="lg">
    <posItemsGrid :loading="cleanupDialogLoading" :showFoundArchived="true" :items="posItemsCleanupPreview" :gridStyle="{ height: `calc(100vh - 150px)` }" disableSelection />
  </wiskModal>

  <wiskModal title="Parent venue data preview for conversion to hotel shared items" :visible="Boolean(convertToHotelSharedItemsPreviewData)" @hide="convertToHotelSharedItemsPreviewData = null" @ok="onConvertToHotelSharedItemsPreviewOk" preventOKClose size="lg" :okText="convertPreviewOkText" :okDisabled="convertPreviewOkDisabled">
    <p>This is a preview of the parent venue data after conversion, please review and confirm the conversion.</p>

    <div v-if="hasUnresolvedVariationConflicts" class="mb-4">
      <div class="d-flex justify-content-between align-items-start mb-1">
        <h5 class="text-danger mb-0">{{ translations.txtVenueOperationsVariationConflictsTitle }}</h5>
        <b-button variant="outline-secondary" size="sm" @click="exportVariationConflicts" :disabled="convertLoading">
          {{ translations.txtGenericExport }} (CSV)
        </b-button>
      </div>
      <p class="text-danger">{{ translations.txtVenueOperationsVariationConflictsDescription }}</p>
      <div v-for="conflict in convertToHotelSharedItemsPreviewData?.variation_conflicts || []" :key="conflict.item_variation_id" class="mb-3 p-2 border rounded">
        <div class="fw-bold">{{ translations.translate('tplVenueOperationsVariationConflictsItemVariationLabel', { '{a}': conflict.item_title, '{b}': conflict.variation_title }) }}</div>
        <div class="text-muted small mb-2">{{ translations.txtVenueOperationsVariationConflictsItemVariationId }}: {{ conflict.item_variation_id }}</div>
        <div v-for="(variant, variantIndex) in conflict.variants" :key="variantIndex" class="ms-2 mb-1">
          <label>
            <input type="radio" :name="`conflict-${conflict.item_variation_id}`" :value="variantIndex"
                v-model="selectedVariantPerConflict[conflict.item_variation_id]"
                :disabled="appliedConflictIds[conflict.item_variation_id] || applyingConflictIds[conflict.item_variation_id]" />
            <strong>{{ variant.measurement.quantity }} {{ variant.measurement.unit_of_measurement }}</strong>
            &middot; {{ translations.txtVenueOperationsVariationConflictsCaseSize }} {{ variant.case_size }}
            <span class="text-muted">({{ formatVariantVenues(variant.venues) }})</span>
          </label>
        </div>
        <b-button v-if="!appliedConflictIds[conflict.item_variation_id]" variant="outline-primary" size="sm" class="mt-2"
            :disabled="convertLoading || applyingConflictIds[conflict.item_variation_id] || selectedVariantPerConflict[conflict.item_variation_id] === undefined"
            @click="applyConflictResolution(conflict)">
          {{ translations.txtVenueOperationsVariationConflictsApply }}
          <wiskLoading v-if="applyingConflictIds[conflict.item_variation_id]" loading class="position-relative float-end ms-2" />
        </b-button>
        <div v-else class="text-success small mt-2">Applied &mdash; recalculate preview to refresh</div>
      </div>
      <wiskLoading v-if="convertLoading" loading class="position-relative" />
    </div>

    <div v-for="(value, key) in convertToHotelSharedItemsPreviewData?.new_data_on_parent || {}" :key="key" class="mb-3">
      <h5>{{ translations.translate(`groupVenueOperationsConvertToHotelSharedItemsPreview.${key}`) }}</h5>
      <wiskGrid :columnDefs="[{ headerName: 'Title', sortOrder: 1, colId: 'title', field: 'title' }]" :rowData="value" parentGridName="previewConvertToHotelSharedItems" trackBy="title" :header="{}" :gridStyle="{ height: '400px' }" />
    </div>
  </wiskModal>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import merge from 'lodash.merge'
import get from 'lodash.get'
import api from '@/api'
import { promptDownload } from '@/modules/utils'
import paymentInfo from '@/components/venues/VenuePaymentInfo'
import barcodePrinterSetup from '@/components/venues/BarcodePrinterSetup'
import venueFinder from '@/components/venues/VenueFinder'
import infoTooltip from '@/components/common/WiskInfoTooltip'
import customFields from '@/components/customFields/CustomFieldsRender'
import venuesSharedCustomFields from '@/components/customFields/VenuesSharedCustomFields'
import imageView from '@/components/common/ImageView'
import posItemsGrid from '@/components/posItems/POSItemsGrid.vue'
import venuePOSIntegrations from '@/components/venues/VenuePOSIntegrations'

export default {
  name: 'VenueOperations',
  emits: ['validStateChanged', 'refresh', 'singleOperation', 'input'],
  components: { venuePOSIntegrations, posItemsGrid, venueFinder, paymentInfo, infoTooltip, customFields, imageView, barcodePrinterSetup, venuesSharedCustomFields },
  props: {
    disabled: Boolean,
    admin: Boolean,
    venue: Object,
    preloadTitle: String,
    currentVenue: Object
  },
  data() {
    return {
      // Mirror AIChatUsageCap.defaultMonthlyLimitCents / maxMonthlyLimitCents in wisk-api.
      aiChatDefaultMonthlyLimitCents: 2000,
      aiChatMaxMonthlyLimitCents: 50000,
      localVenue: { use_parent_items: false },
      syncProviders: [
        { id: 'firestore', title: 'firestore' },
        { id: 'couchbase', title: 'couchbase' },
        { id: 'wisk-sync', title: 'wisk-sync' },
        { id: 'hybrid', title: 'hybrid' },
      ],
      accountingIntegrationTypes: [
        { id: 'quickbooks', title: 'QuickBooks Online' },
        { id: 'quickbooks_desktop', title: 'QuickBooks Desktop' }
      ],
      draftInvoiceSettingTypes: [
        { id: 'none', title: 'None' },
        { id: 'venue', title: 'Venue' },
        { id: 'wisk', title: 'Wisk' },
      ],
      operations: [],
      localOperations: [],
      venueFindOperations: [],
      posComponentKey: 1,
      posComponentKeySecondary: 1,
      paymentOperations: [],
      valid: true,
      initDone: false,
      resyncVenueDataIncreaseVersion: false,
      showApiKey: false,
      populateMethod: 0,
      organizations: [],
      loadingOrganizations: false,
      posItemsCleanupDialog: false,
      posItemsCleanupPreview: [],
      cleanupDialogLoading: false,
      selectedChildVenues: [],
      convertToHotelSharedItemsPreviewData: null,
      convertLoading: false,
      convertPreviewProgress: 0,
      selectedVariantPerConflict: {},
      applyingConflictIds: {},
      appliedConflictIds: {},
      shortTitleMaxLength: 30,
      inputGroupErrorCount: 0
    }
  },
  computed: {
    ...mapGetters([
      'languages',
      'timeZones',
      'venueTypes',
      'onboardingTypes',
      'statuses',
      'partialMeasuringTypes',
      'currencies',
      'salesReps',
      'measurementsByType'
    ]),
    ...mapState(['posItemsById', 'customFieldsByTarget', 'user', 'translations', 'currentPermissionsByType', 'accountingVenueClasses', 'venuesHighLevel']),
    locationCodeValidation() {
      // Mirrors the API: 4 to 6 letters or digits (uppercased on save), empty clears it. "NULL" is reserved by the API.
      return {
        validator: value => {
          const code = (value || '').trim().toUpperCase()
          return !code || (/^[A-Z0-9]{4,6}$/.test(code) && code !== 'NULL')
        },
        message: this.translations.txtVenueOperationsLocationCodeInvalid
      }
    },
    missingLocationCodeForNewHotelVenue() {
      if (this.localVenue?.id || !this.localVenue?.is_part_of_hotel) {
        return false
      }
      return !this.locationCodeValidation.validator(this.localVenue.location_code) || !(this.localVenue.location_code || '').trim()
    },
    partOfHotelValidation() {
      // Hotel venues (the requisition app) need a location code; the API rejects turning this on without one.
      return {
        validator: value => !value || !!this.localVenue?.location_code,
        message: this.translations.txtVenueOperationsLocationCodeRequiredForHotel
      }
    },
    shortTitleValidation() {
      return {
        validator: value => !value || value.trim().length <= this.shortTitleMaxLength,
        message: `Please use ${this.shortTitleMaxLength} characters or fewer.`
      }
    },
    newVenueDataPopulateMethods() {
      return [
        { id: 0, title: this.translations.txtVenueOperationsPopulateDataNew },
        { id: 1, title: this.translations.txtVenueOperationsPopulateDataCopy },
        { id: 2, title: this.translations.txtVenueOperationsPopulateDataShare }
      ]
    },
    hasUnresolvedVariationConflicts() {
      return (this.convertToHotelSharedItemsPreviewData?.variation_conflicts || []).length > 0
    },
    hasApplyingConflicts() {
      return Object.values(this.applyingConflictIds).some(Boolean)
    },
    convertPreviewOkText() {
      return this.hasUnresolvedVariationConflicts ? 'Recalculate preview' : 'Confirm conversion'
    },
    convertPreviewOkDisabled() {
      return this.convertLoading || this.hasApplyingConflicts
    },
    measurementsVolume() {
      return this.measurementsByType.volume?.units_of_measurements.filter(m => ![ 'cup', 'tbsp', 'tsp'].includes(m.id))
    },
    measurementsWeight() {
      return this.measurementsByType.weight?.units_of_measurements
    },
    addNewOrganizationMultiselectAction() {
      return {
        action: (id, searchQuery, value, callbackItemInjected) => {
          this.setGlobalAction({
            type: 'organizationEdit',
            action: {
              id: 0,
              title: searchQuery,
              onChange: organization => {
                callbackItemInjected(organization)
              }
            }
          })
        },
        label: this.translations.txtOrganizationsNew
      }
    },
    venuesForPOSIngredientsCopy() {
      return this.user.user_venues.filter(v => v.id !== this.currentVenue.id).map(uv => uv.venue)
    },
    hasOtherVenues() {
      return !!this.user.user_venues.length
    },
    showChangeSubscriptionPlatform() {
      return get(this.localVenue, 'subscription.selected_plan.payment_platform') === 'apple'
    },
    godMode() {
      return !!this.user?.god_mode
    },
    // The venue's ai_chat override in cents, or null when it is on the default allowance.
    aiChatMonthlyLimitCents() {
      const override = (this.venue?.subscription?.custom_plan_features || []).find(f => f?.feature_identifier === 'ai_chat')
      return typeof override?.limit === 'number' ? override.limit : null
    },
    // Shown in the field: the number input can't be blank (it turns empty into 0), so it shows the limit
    // actually in force and the default is restored with the reset button instead.
    aiChatEffectiveMonthlyLimitCents() {
      return this.aiChatMonthlyLimitCents === null ? this.aiChatDefaultMonthlyLimitCents : this.aiChatMonthlyLimitCents
    },
    aiChatMonthlyLimitHelperText() {
      if (this.aiChatMonthlyLimitCents === null) {
        return 'Default allowance: model cost per calendar month (UTC).'
      }
      if (this.aiChatMonthlyLimitCents === 0) {
        return 'AI chat is turned off for this venue.'
      }
      return 'Custom limit: model cost per calendar month (UTC).'
    },
    accountingIntegrated() {
      return this.venue?.accounting_integration?.status === 'active'
    },
    selectedCurrency() {
      if (this.currencies && this.currencies.length && this.localVenue && this.localVenue.display_currency) {
        return this.currencies.find(c => c.code === this.localVenue.display_currency.code)
      }
      return null
    },
    priceFeeAvailable() {
      if (this.venue && this.venue.country === 'CA' && this.venue.state === 'QC') {
        return true
      }
      return false
    },
    copyDataOptions() {
      return [
        { title: this.translations.translate('tplVenueOperationsCopyAllList', { '{a}': this.currentVenue.title }), id: 'all' },
        { title: this.translations.translate('tplVenueOperationsCopyBatchesList', { '{a}': this.currentVenue.title }), id: 'batches' },
        { title: this.translations.translate('tplVenueOperationsCopyItemsList', { '{a}': this.currentVenue.title }), id: 'items' }
      ]
    }
  },
  methods: {
    ...mapActions(['setGlobalAction', 'resetSyncProviderLocalPersistence', 'notify']),
    beerSaverIntegrationChanged(value) {
      if (value) {
        this.onSingleChange({
            'type': 'beer_saver_integration',
            'value': {
                'type': 'logo'
            }
        })
      } else {
        this.onSingleChange({
            'type': 'beer_saver_integration_clear'
        })
      }
    },
    loadCleanupPOSItemsPreview() {
      this.posItemsCleanupDialog = true
      this.cleanupDialogLoading = true
      this.posItemsCleanupPreview = []
      api.cleanUnusedPOSItemsPreview().then(response => {
        const temp = []
        response.pos_item_ids.forEach(p => {
          temp.push(this.posItemsById[p])
        })
        this.posItemsCleanupPreview = temp
        this.cleanupDialogLoading = false
      })
    },
    confirmCleanupPOSItems() {
      this.posItemsCleanupDialog = false
      api.cleanUnusedPOSItemsConfirm()
    },
    posRetry() {
      api.posRetry(this.venue.id).then(() => {
        this.notify({
          message: 'POS retry started.',
          type: 'info'
        })
      })
    },
    refreshVenue() {
      this.$emit('refresh')
    },
    setValidState(errorCount) {
      this.inputGroupErrorCount = errorCount
      this.emitValidState()
    },
    emitValidState() {
      // A newly shown, still empty required input reports itself valid to the input group until it is typed in,
      // so the group alone would let a hotel venue be created without a location code (and the API would refuse it).
      this.valid = !this.inputGroupErrorCount && !this.missingLocationCodeForNewHotelVenue
      this.$emit('validStateChanged', this.valid)
    },
    editVenueImage() {
      if (!this.disabled) {
        this.setGlobalAction({
          type: 'fileUpload',
          action: {
            fileTypesAccept: 'image/*',
            useGoogle: true,
            target: { image: this.localVenue.white_label },
            save: value => {
              this.onSingleChange({ type: 'white_label', value })
            }
          }
        })
      }
    },
    removeVenueImage() {
      if (this.$refs.confirmDialog) {
        this.$refs.confirmDialog.confirm({
          message: this.translations.confirmRemoveImages,
          title: this.translations.confirmRemoveTitle,
          callback: () => {
            this.onSingleChange({ type: 'white_label_clear' })
          },
        })
      }
    },
    paymentInfoChanged(operations) {
      if (this.user.god_mode) {
        if (this.venue.id) {
          this.$emit('singleOperation', operations)
        } else {
          this.paymentOperations = operations
        }
      }
    },
    venueFinderChanged(operations) {
      if (this.venue.id) {
        this.$emit('singleOperation', operations)
      } else {
        this.venueFindOperations = operations
      }
    },
    onDraftInvoiceSettingChange(draftInvoiceSettingType) {
      let enabled = draftInvoiceSettingType !== 'none',
        type = enabled ? draftInvoiceSettingType : undefined,
        value = { enabled, type },
        operation = { type: 'draft_invoices_settings', value }

      if (this.localVenue.id) {
        this.onSingleChange(operation)
      } else {
        setTimeout(() => {
          if (this.$refs.inputGroup?.setOperation) {
            this.$refs.inputGroup.setOperation({ operation, allowDuplicateOperationType: false, operationTypes: { set: 'draft_invoices_settings' } })
          }
        }, 100)
      }
    },
    onSingleChange(operation) {
      if (this.venue.id) {
        this.$emit('singleOperation', operation)
      }
    },
    // The input works in dollars, the API in cents. 0 is a real limit (assistant off). Re-entering the
    // default on a venue without an override saves nothing, so it keeps following the default.
    onAiChatMonthlyLimitChange({ value }) {
      const cents = Math.round(Number(value) * 100)
      if (Number.isFinite(cents) && cents !== this.aiChatEffectiveMonthlyLimitCents) {
        this.onSingleChange({ type: 'ai_chat_monthly_limit', value: cents })
      }
    },
    onUseParentItemsChange() {
      setTimeout(() => {
        if (!this.localVenue.use_parent_items) {
          this.localOperations = this.localOperations.filter(o => o.type !== 'use_parent_pos_items' && o.type !== 'use_parent_items')
        }
      }, 0)
    },
    onPopulateMethodChange() {
      if (!this.populateMethod) {
        this.localVenue.copy_data_from_venue = false
        this.localVenue.use_parent_items = false
        this.localVenue.use_parent_pos_items = false
      }

      if (this.populateMethod === 2) {
        this.localVenue.use_parent_items = true
      }
    },
    onChange(operations) {
      if (this.populateMethod !== 1) {
        this.localOperations = operations.filter(o => o.type !== 'copy_data_from_venue')
      } else if (this.populateMethod !== 2) {
        this.localOperations = operations.filter(o => o.type !== 'use_parent_pos_items' && o.type !== 'use_parent_items')
      }
    },
    mergeOperations() {
      this.operations = [...this.localOperations, ...this.venueFindOperations, ...this.paymentOperations]
    },
    goToQuickbooks() {
      let tabOpen = window.open('about:blank', 'newtab')

      api.quickbooksAuth().then(result => {
        if (result && result.url) {
          tabOpen.location = result.url
        }
      })
    },
    resyncVenueData() {
      if (this.$refs.confirmDialogResyncVenueData) {
        this.$refs.confirmDialogResyncVenueData.confirm({
          message: 'Are you sure you want to resync venue data? This process might take a while.',
          title: 'Confirm Resync',
          callback: () => {
            api.couchBaseResync(this.venue.id, this.resyncVenueDataIncreaseVersion).then(() => {
              this.notify({
                message: 'Resyncing venue data. This may take a few minutes.',
                type: 'info'
              })
            })
          },
        })
      }
    },
    resyncAreasData() {
      api.resyncAreas().then(() => {
        this.notify({
          message: 'Resyncing areas data. This may take a few minutes.',
          type: 'info'
        })
      })
    },
    resetLocalData() {
      if (this.$refs.confirmDialog) {
        this.$refs.confirmDialog.confirm({
          message: this.translations.confirmResyncVenueData,
          title: this.translations.confirmResyncVenueDataTitle,
          callback: () => {
            this.resetSyncProviderLocalPersistence()
          },
        })
      }
    },
    sendVenueReport(type) {
      if (type === 'weekly') {
        api.sendVenueWeeklyReport().then(() => {
          this.notify({
            message: 'Venue weekly report will be sent to your email address.',
            type: 'success'
          })
        })
      } else {
        api.sendVenueDailyReport().then(() => {
          this.notify({
            message: 'Venue daily report will be sent to your email address.',
            type: 'success'
          })
        })
      }
    },
    confirmConvertToHotelSharedItems() {
      if (this.$refs.confirmDialog) {
        this.$refs.confirmDialog.confirm({
          message: 'Are you sure you want to convert the venue and selected child venues to hotel shared items structure?',
          title: 'Confirm Conversion',
          callback: () => {
            this.convertLoading = true
            api.convertToHotelSharedItems({
              'parent_venue_id': this.venue.id,
              'child_venue_ids': this.selectedChildVenues.filter(vId => vId !== this.venue.id),
              'convert_to_hotel': true
            }).then(() => {
              this.convertLoading = false
              this.selectedChildVenues = []
              this.convertToHotelSharedItemsPreviewData = null
              this.notify({
                message: 'Conversion started. This may take a while. You can track the progress in the "Background Jobs" dropdown in header.',
                type: 'success'
              })
            })
            .catch(() => {
              this.convertLoading = false
            })
          },
        })
      }
    },
    previewConvertToHotelSharedItems() {
      if (this.$refs.confirmDialogChildVenueSelector) {
        this.$refs.confirmDialogChildVenueSelector.confirm({
          message: 'Select the child venues to convert to hotel shared items structure.',
          title: 'Convert to Hotel Shared Items Structure',
          callback: () => {
            this.selectedVariantPerConflict = {}
            this.runPreviewConvertToHotelSharedItems()
          },
        })
      }
    },
    async runPreviewConvertToHotelSharedItems() {
      this.convertLoading = true
      this.convertPreviewProgress = 0
      try {
        const { job_id: jobId } = await api.previewConvertToHotelSharedItems({
          'parent_venue_id': this.venue.id,
          'child_venue_ids': this.selectedChildVenues.filter(vId => vId !== this.venue.id),
          'convert_to_hotel': true
        })
        const result = await this.pollConvertToHotelSharedItemsPreview(jobId)
        if (result) {
          Object.keys(result.new_data_on_parent).forEach(key => {
            result.new_data_on_parent[key] = result.new_data_on_parent[key].filter(item => item.title)
          })
          this.convertToHotelSharedItemsPreviewData = result
          this.selectedVariantPerConflict = {}
          this.appliedConflictIds = {}
          this.applyingConflictIds = {}
        }
      } catch {
        // HTTP errors are surfaced by the api response interceptor
      } finally {
        this.convertLoading = false
        this.convertPreviewProgress = 0
      }
    },
    pollConvertToHotelSharedItemsPreview(jobId) {
      return new Promise((resolve, reject) => {
        const poll = () => {
          api.convertToHotelSharedItemsPreviewStatus(jobId).then(job => {
            if (job.status === 'completed') {
              resolve(job.result)
            } else if (job.status === 'failed') {
              this.notify({ message: job.error || 'Convert to hotel shared items preview failed.', type: 'error' })
              resolve(null)
            } else {
              this.convertPreviewProgress = job.progress || 0
              setTimeout(poll, 1500)
            }
          }).catch(reject)
        }
        poll()
      })
    },
    onConvertToHotelSharedItemsPreviewOk() {
      if (this.hasUnresolvedVariationConflicts) {
        this.runPreviewConvertToHotelSharedItems()
      } else {
        this.confirmConvertToHotelSharedItems()
      }
    },
    async applyConflictResolution(conflict) {
      const conflictId = conflict.item_variation_id
      const variant = this.selectedVariantFor(conflict)
      if (!variant || this.convertLoading || this.applyingConflictIds[conflictId] || this.appliedConflictIds[conflictId]) {
        return
      }
      this.applyingConflictIds = { ...this.applyingConflictIds, [conflictId]: true }
      try {
        const { job_id: jobId } = await api.resolveConvertToHotelSharedItemsVariationConflict({
          'parent_venue_id': this.venue.id,
          'child_venue_ids': this.selectedChildVenues.filter(vId => vId !== this.venue.id),
          'item_variation_id': conflictId,
          'measurement': variant.measurement,
          'case_size': variant.case_size
        })
        const job = await this.pollResolveVariationConflict(jobId)
        if (job?.status === 'completed') {
          this.appliedConflictIds = { ...this.appliedConflictIds, [conflictId]: true }
        } else if (job?.status === 'failed') {
          this.notify({ message: job.error || 'Resolving the variation conflict failed.', type: 'error' })
        }
      } catch {
        // HTTP errors are surfaced by the api response interceptor
      } finally {
        const next = { ...this.applyingConflictIds }
        delete next[conflictId]
        this.applyingConflictIds = next
      }
    },
    pollResolveVariationConflict(jobId) {
      return new Promise((resolve, reject) => {
        const poll = () => {
          api.resolveConvertToHotelSharedItemsVariationConflictStatus(jobId).then(job => {
            if (job.status === 'completed' || job.status === 'failed') {
              resolve(job)
            } else {
              setTimeout(poll, 1500)
            }
          }).catch(reject)
        }
        poll()
      })
    },
    exportVariationConflicts() {
      const conflicts = this.convertToHotelSharedItemsPreviewData?.variation_conflicts || []
      if (!conflicts.length) {
        return
      }
      const headers = ['Item', 'Item ID', 'Variation', 'Item variation ID', 'Measurement', 'Case size', 'Venues']
      const rows = []
      conflicts.forEach(conflict => {
        (conflict.variants || []).forEach(variant => {
          rows.push([
            conflict.item_title,
            conflict.item_id,
            conflict.variation_title,
            conflict.item_variation_id,
            `${variant.measurement?.quantity ?? ''} ${variant.measurement?.unit_of_measurement ?? ''}`.trim(),
            variant.case_size,
            this.formatVariantVenues(variant.venues || [])
          ])
        })
      })
      const escapeCell = value => {
        const str = value === null || value === undefined ? '' : String(value)
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
      }
      const csv = [headers, ...rows].map(row => row.map(escapeCell).join(',')).join('\n')
      const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' })
      promptDownload({ blob, fileName: `variation-conflicts-venue-${this.venue.id}.csv` })
    },
    selectedVariantFor(conflict) {
      const variantIndex = this.selectedVariantPerConflict[conflict.item_variation_id]
      if (variantIndex === undefined) {
        return null
      }
      return conflict.variants[variantIndex] || null
    },
    formatVariantVenues(venues) {
      const parentBadge = this.translations.txtVenueOperationsVariationConflictsParentBadge
      return venues.map(venue => venue.is_parent ? `${venue.venue_title} (${parentBadge})` : venue.venue_title).join(', ')
    }
  },
  created() {
    if (this.user?.god_mode) {
      this.loadingOrganizations = true
      api.organizations().then(data => {
        this.organizations = data
      }).finally(() => {
        this.loadingOrganizations = false
      })
    }
  },
  watch: {
    populateMethod: 'onPopulateMethodChange',
    missingLocationCodeForNewHotelVenue: 'emitValidState',
    operations(operations) {
      if (!this.venue.id) {
        this.$emit('input', operations)
      }
    },
    venue: {
      handler() {
        this.localVenue = merge({ subscription: {} }, this.venue, {
          pos_integration_status: get(this.venue, 'pos.status.type', 'no'),
          draft_invoices_settings: merge({}, { enabled: false, type: 'none' }, this.venue.draft_invoices_settings)
        })

        if (!this.localVenue.id) {
          this.onDraftInvoiceSettingChange('venue')
          this.localVenue.draft_invoices_settings = { enabled: true, type: 'venue' }
        }

        if (this.localVenue.payment) {
          this.payment = { ...this.localVenue.payment }
        }
        this.initDone = true
      },
      immediate: true
    },
    localOperations(operations) {
      if (Array.isArray(operations)) {
        this.mergeOperations()
      }
    },
    venueFindOperations(operations) {
      if (Array.isArray(operations)) {
        this.mergeOperations()
      }
    },
    paymentOperations(operations) {
      if (Array.isArray(operations)) {
        this.mergeOperations()
      }
    }
  }
}
</script>

<style lang="scss">
.venue-settings-col {
  // min-width: 200px;
  min-height: 55px;
}
</style>
